// A small verlet ragdoll on the RIG.md skeleton (docs/DESIGN.md §7: "Ragdoll = physics (verlet) on the same rig") —
// the Popper's comedy: a popped innocent tumbles over, a caught Sneak's true self flies across the square.
// Particles sit on the joints; the torso is a rigid cage (all pairwise distances held), limbs are distance chains;
// the ground is y = 0 with friction. Each frame the bones are re-aimed from their start pose toward the particles.

import * as THREE from "three";

const J = ["hips", "chest", "head", "upperArmL", "lowerArmL", "handL", "upperArmR", "lowerArmR", "handR", "upperLegL", "lowerLegL", "footL", "upperLegR", "lowerLegR", "footR", "headTop"] as const;
type JName = typeof J[number];
const TORSO: JName[] = ["hips", "chest", "upperArmL", "upperArmR", "upperLegL", "upperLegR"];
const LINKS: [JName, JName][] = [
  ["chest", "head"], ["head", "headTop"], ["chest", "headTop"],
  ["upperArmL", "lowerArmL"], ["lowerArmL", "handL"], ["upperArmR", "lowerArmR"], ["lowerArmR", "handR"],
  ["upperLegL", "lowerLegL"], ["lowerLegL", "footL"], ["upperLegR", "lowerLegR"], ["lowerLegR", "footR"],
  // soft limits: keep limbs from folding through the body
  ["hips", "lowerLegL"], ["hips", "lowerLegR"], ["chest", "lowerArmL"], ["chest", "lowerArmR"],
];
// bone → the joint its direction points at (for re-aiming)
const AIM: Record<string, JName> = {
  head: "headTop", upperArmL: "lowerArmL", lowerArmL: "handL", upperArmR: "lowerArmR", lowerArmR: "handR",
  upperLegL: "lowerLegL", lowerLegL: "footL", upperLegR: "lowerLegR", lowerLegR: "footR",
};
const GRAV = -14, FRICTION = 0.82, ITER = 6;

interface P { p: THREE.Vector3; o: THREE.Vector3 }

export class Ragdoll {
  private pts = new Map<JName, P>();
  private cons: { a: P; b: P; d: number; stiff: number }[] = [];
  private bones = new Map<string, THREE.Bone>();
  private q0 = new Map<string, THREE.Quaternion>();     // bone world quaternions at start
  private d0 = new Map<string, THREE.Vector3>();        // bone → aim joint world direction at start
  private torso0 = new THREE.Matrix4();
  private hipsQ0 = new THREE.Quaternion();
  private spineChain: THREE.Bone[] = [];
  private spineRel: THREE.Quaternion[] = [];
  t = 0;
  private settle = 0;

  /** start from the skeleton's current pose; `impulse` is the launch velocity (m/s) added to every particle */
  constructor(private root: THREE.Object3D, skeleton: THREE.Skeleton, impulse: THREE.Vector3, spin = 0) {
    for (const b of skeleton.bones) this.bones.set(b.name, b);
    root.updateMatrixWorld(true);
    const wp = (n: string) => this.bones.get(n)!.getWorldPosition(new THREE.Vector3());
    const pos: Record<string, THREE.Vector3> = {};
    for (const n of J) {
      if (n === "headTop") { const h = this.bones.get("head")!; pos[n] = new THREE.Vector3(0, 0.36, 0).applyMatrix4(h.matrixWorld); continue; }
      pos[n] = wp(n);
    }
    // a little spin: the hands and feet get a tangential kick around the hips so bodies cartwheel
    const c = pos.hips;
    for (const n of J) {
      const v = impulse.clone();
      if (spin) { const r = pos[n].clone().sub(c); v.add(new THREE.Vector3(-r.y, r.x, 0).multiplyScalar(spin)); }
      const p = pos[n].clone();
      this.pts.set(n, { p, o: p.clone().addScaledVector(v, -1 / 60) });
    }
    const link = (a: JName, b: JName, stiff = 1) => { const A = this.pts.get(a)!, B = this.pts.get(b)!; this.cons.push({ a: A, b: B, d: A.p.distanceTo(B.p), stiff }); };
    for (let i = 0; i < TORSO.length; i++) for (let k = i + 1; k < TORSO.length; k++) link(TORSO[i], TORSO[k]);
    for (const [a, b] of LINKS) link(a, b, a === "hips" || a === "chest" ? 0.25 : 1);
    for (const [bone, joint] of Object.entries(AIM)) {
      const b = this.bones.get(bone); if (!b) continue;
      this.q0.set(bone, b.getWorldQuaternion(new THREE.Quaternion()));
      this.d0.set(bone, pos[joint].clone().sub(wp(bone)).normalize());
    }
    this.torso0 = this.torsoBasis();
    this.hipsQ0 = this.bones.get("hips")!.getWorldQuaternion(new THREE.Quaternion());
    // spine / chest / neck follow the torso cage rigidly
    for (const n of ["spine", "chest", "neck"]) { const b = this.bones.get(n); if (b) this.spineChain.push(b); }
    const hq = this.hipsQ0.clone().invert();
    this.spineRel = this.spineChain.map((b) => hq.clone().multiply(b.getWorldQuaternion(new THREE.Quaternion())));
  }

  private torsoBasis(): THREE.Matrix4 {
    const g = (n: JName) => this.pts.get(n)!.p;
    const up = g("chest").clone().sub(g("hips")).normalize();
    const side = g("upperArmL").clone().sub(g("upperArmR")).add(g("upperLegL").clone().sub(g("upperLegR"))).normalize();
    const fwd = new THREE.Vector3().crossVectors(side, up).normalize();
    side.crossVectors(up, fwd).normalize();
    return new THREE.Matrix4().makeBasis(side, up, fwd);
  }

  step(dt: number) {
    this.t += dt;
    const h = Math.min(dt, 1 / 30);
    for (const pt of this.pts.values()) {
      const v = pt.p.clone().sub(pt.o);
      pt.o.copy(pt.p);
      pt.p.add(v.multiplyScalar(0.995)).y += GRAV * h * h;
    }
    for (let it = 0; it < ITER; it++) {
      for (const c of this.cons) {
        const d = c.b.p.clone().sub(c.a.p); const L = d.length() || 1e-6;
        const k = ((L - c.d) / L) * 0.5 * c.stiff;
        c.a.p.addScaledVector(d, k); c.b.p.addScaledVector(d, -k);
      }
      for (const pt of this.pts.values()) if (pt.p.y < 0.05) {        // ground: push out + friction on the slide
        pt.p.y = 0.05;
        pt.o.x = pt.p.x - (pt.p.x - pt.o.x) * FRICTION; pt.o.z = pt.p.z - (pt.p.z - pt.o.z) * FRICTION;
        if (pt.o.y < pt.p.y) pt.o.y = pt.p.y + (pt.o.y - pt.p.y) * -0.35;   // a little bounce
      }
    }
    this.pose();
  }

  /** world position of the hips particle (the camera/FX can follow a flying Sneak) */
  get center() { return this.pts.get("hips")!.p; }

  private pose() {
    const rootInv = new THREE.Matrix4().copy(this.root.matrixWorld).invert();
    const hips = this.bones.get("hips")!;
    // hips: position from the particle, rotation from the torso cage
    const rel = new THREE.Matrix4().multiplyMatrices(this.torsoBasis(), new THREE.Matrix4().copy(this.torso0).invert());
    const qRel = new THREE.Quaternion().setFromRotationMatrix(rel);
    const hipsW = qRel.clone().multiply(this.hipsQ0);
    this.setWorld(hips, this.pts.get("hips")!.p.clone(), hipsW, rootInv);
    this.spineChain.forEach((b, i) => this.setWorldQ(b, hipsW.clone().multiply(this.spineRel[i])));
    // limbs: aim each bone from its start direction to the current one
    for (const [bone, joint] of Object.entries(AIM)) {
      const b = this.bones.get(bone); if (!b) continue;
      b.updateWorldMatrix(true, false);
      const from = b.getWorldPosition(new THREE.Vector3());
      const d1 = this.pts.get(joint)!.p.clone().sub(from).normalize();
      const q = new THREE.Quaternion().setFromUnitVectors(this.d0.get(bone)!, d1).multiply(this.q0.get(bone)!);
      // a limb rides its parent's twist: re-base on how much the torso turned
      this.setWorldQ(b, q);
    }
  }
  private setWorld(b: THREE.Bone, wpos: THREE.Vector3, wq: THREE.Quaternion, rootInv: THREE.Matrix4) {
    const parent = b.parent!; parent.updateWorldMatrix(true, false);
    const pInv = new THREE.Matrix4().copy(parent.matrixWorld).invert();
    b.position.copy(wpos.applyMatrix4(pInv));
    this.setWorldQ(b, wq);
    void rootInv;
  }
  private setWorldQ(b: THREE.Bone, wq: THREE.Quaternion) {
    const pq = b.parent!.getWorldQuaternion(new THREE.Quaternion()).invert();
    b.quaternion.copy(pq.multiply(wq));
    b.updateMatrixWorld(true);
  }
}
